/**
 * Backend Health Service - Monitor API availability and show banner
 * Pings /health every 30s and manages offline state
 */

import { apiClient } from '../lib/api';

export interface HealthStatus {
  isOnline: boolean;
  lastChecked: Date;
  error?: string;
  responseTime?: number;
  consecutiveFailures?: number;
}

type HealthListener = (status: HealthStatus) => void;

class BackendHealthService {
  private status: HealthStatus = {
    isOnline: true,
    lastChecked: new Date(),
    consecutiveFailures: 0,
  };
  
  private listeners: HealthListener[] = [];
  private checkInterval: NodeJS.Timeout | null = null;
  private readonly CHECK_INTERVAL_MS = 30000; // 30 seconds
  private readonly FAILURE_THRESHOLD = 2; // avoid false "offline" on transient blips
  private inFlightCheck: Promise<HealthStatus> | null = null;
  private consecutiveFailures = 0;

  /**
   * Start monitoring backend health
   */
  start(): void {
    // Idempotent start (prevents duplicate intervals on remounts/navigation)
    if (this.checkInterval) return;

    // Initial check
    void this.checkHealth();
    
    // Set up periodic checks
    this.checkInterval = setInterval(() => {
      void this.checkHealth();
    }, this.CHECK_INTERVAL_MS);
  }

  /**
   * Stop monitoring
   */
  stop(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Get current health status
   */
  getStatus(): HealthStatus {
    return { ...this.status };
  }

  /**
   * Subscribe to health status changes
   */
  subscribe(listener: HealthListener): () => void {
    this.listeners.push(listener);
    // Push current status immediately so UI doesn't wait for next state transition.
    listener(this.getStatus());
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Manual health check
   */
  async checkHealth(): Promise<HealthStatus> {
    if (this.inFlightCheck) {
      return this.inFlightCheck;
    }

    const startTime = Date.now();

    this.inFlightCheck = (async () => {
      try {
        await apiClient.checkHealth();

        const responseTime = Date.now() - startTime;
        this.consecutiveFailures = 0;
        this.updateStatus({
          isOnline: true,
          lastChecked: new Date(),
          responseTime,
          error: undefined,
          consecutiveFailures: 0,
        });
      } catch (error) {
        this.consecutiveFailures += 1;
        const shouldMarkOffline =
          !this.status.isOnline || this.consecutiveFailures >= this.FAILURE_THRESHOLD;

        this.updateStatus({
          isOnline: shouldMarkOffline ? false : this.status.isOnline,
          lastChecked: new Date(),
          error: error instanceof Error ? error.message : 'Unknown error',
          responseTime: undefined,
          consecutiveFailures: this.consecutiveFailures,
        });
      } finally {
        this.inFlightCheck = null;
      }

      return this.getStatus();
    })();

    return this.inFlightCheck;
  }

  private updateStatus(newStatus: HealthStatus): void {
    const prev = this.status;
    this.status = { ...newStatus };

    // Notify on meaningful changes (including error/check updates).
    if (
      prev.isOnline !== newStatus.isOnline ||
      prev.error !== newStatus.error ||
      prev.responseTime !== newStatus.responseTime ||
      prev.lastChecked.getTime() !== newStatus.lastChecked.getTime() ||
      prev.consecutiveFailures !== newStatus.consecutiveFailures
    ) {
      this.notifyListeners();
    }
  }

  private notifyListeners(): void {
    const status = this.getStatus();
    this.listeners.forEach(listener => {
      try {
        listener(status);
      } catch (error) {
        console.error('Error notifying health status listener:', error);
      }
    });
  }
}

// Export singleton instance
export const backendHealthService = new BackendHealthService();
